// The create-a-person form, shared by the People page, the household member editor, and
// step 1 of the create-benefit flow. Duplicate detection lives here rather than in each
// caller: a household member is a real person record, so every entry point has to apply the
// same "resolve the duplicate first" rule the WinForms intake screen applies.
import { skipToken } from "@reduxjs/toolkit/query"
import { benefitApi } from "@/features/benefit-workspace/benefitApi"
import { useBenefitScopeArg, useDebouncedBenefitInput } from "@/features/benefit-workspace/useBenefitChildReads"
import { IconAlertTriangle, IconArrowRight, IconLoader2 } from "@tabler/icons-react"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  canCheckDuplicates,
  duplicateQuery,
  genderOptions,
  maskSsn,
  type PersonIdentityDraft,
  type PotentialPersonMatch,
} from "@/lib/person-identity"

/**
 * Debounced potential-duplicate lookup. Returns an empty list until the draft carries every
 * field the endpoint matches on, because it returns nothing before that anyway.
 */
export function usePotentialDuplicates(draft: PersonIdentityDraft, enabled: boolean) {
  const ready = enabled && canCheckDuplicates(draft)
  const query = ready ? duplicateQuery(draft) : ""
  const settled = useDebouncedBenefitInput(query, 400)
  const scope = useBenefitScopeArg(ready)
  const args = scope !== skipToken && settled === query ? { ...scope, query } : skipToken
  const read = benefitApi.useGetBenefitPotentialDuplicatesQuery(args)
  // Preserve the existing advisory lookup policy: failure is not proof of no duplicates,
  // and the server still validates creation. Never show a previous identity's matches.
  return {
    duplicates: args !== skipToken && !read.isError && !read.isFetching ? read.currentData?.matches ?? [] : [],
    checking: ready && (settled !== query || read.isFetching),
    error: args !== skipToken && read.isError ? "Duplicate lookup unavailable; the server will still validate this person." : null,
  }
}

export function PersonIdentityFields({
  value,
  onChange,
  disabled = false,
  idPrefix = "person",
  canEditFullIdentifiers = true,
}: {
  value: PersonIdentityDraft
  onChange: (next: PersonIdentityDraft) => void
  disabled?: boolean
  idPrefix?: string
  /** Hide full SSN entry for callers that do not hold the identifier permission. */
  canEditFullIdentifiers?: boolean
}) {
  const set = (patch: Partial<PersonIdentityDraft>) => onChange({ ...value, ...patch })
  return (
    <div className="grid gap-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-firstName`}>First Name</Label>
          <Input
            id={`${idPrefix}-firstName`}
            value={value.firstName}
            disabled={disabled}
            onChange={(e) => set({ firstName: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-lastName`}>Last Name</Label>
          <Input
            id={`${idPrefix}-lastName`}
            value={value.lastName}
            disabled={disabled}
            onChange={(e) => set({ lastName: e.target.value })}
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-middleName`}>Middle Name (optional)</Label>
        <Input
          id={`${idPrefix}-middleName`}
          value={value.middleName}
          disabled={disabled}
          onChange={(e) => set({ middleName: e.target.value })}
        />
      </div>
      {canEditFullIdentifiers && <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-ssn`}>Social Security Number (optional)</Label>
        <Input
          id={`${idPrefix}-ssn`}
          placeholder="123-45-6789"
          value={value.socialSecurityNumber}
          disabled={disabled}
          onChange={(e) => set({ socialSecurityNumber: e.target.value })}
        />
      </div>}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-dob`}>Date of Birth</Label>
          <Input
            id={`${idPrefix}-dob`}
            type="date"
            value={value.dateOfBirth}
            disabled={disabled}
            onChange={(e) => set({ dateOfBirth: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-gender`}>Gender</Label>
          <Select
            value={value.gender}
            disabled={disabled}
            onValueChange={(gender) => set({ gender })}
          >
            <SelectTrigger id={`${idPrefix}-gender`}>
              <SelectValue placeholder="Select gender" />
            </SelectTrigger>
            <SelectContent>
              {genderOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  )
}

export function PotentialDuplicates({
  duplicates,
  checking,
  error,
  onSelect,
  selectLabel = "Open this record instead of creating a duplicate, or change the details if this is a different person.",
}: {
  duplicates: PotentialPersonMatch[]
  checking: boolean
  error?: string | null
  onSelect: (match: PotentialPersonMatch) => void
  selectLabel?: string
}) {
  if (checking) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <IconLoader2 className="size-4 animate-spin" />
        Checking for existing records…
      </p>
    )
  }
  if (error) return <p role="status" className="text-sm text-destructive">{error}</p>
  if (duplicates.length === 0) return null

  return (
    <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 dark:bg-amber-950/30">
      <div className="flex items-center gap-2 text-sm font-medium text-amber-700 dark:text-amber-400">
        <IconAlertTriangle className="size-4" />
        {duplicates.length} possible existing {duplicates.length === 1 ? "record" : "records"} found
      </div>
      <p className="text-xs text-muted-foreground">{selectLabel}</p>
      <div className="divide-y rounded-md border bg-background">
        {duplicates.map((match) => (
          <button
            key={match.id}
            type="button"
            onClick={() => onSelect(match)}
            className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-muted/50"
          >
            <span>
              <span className="font-medium">
                {match.lastName}, {match.firstName}
              </span>
              <span className="ml-2 font-mono text-xs text-muted-foreground">
                {maskSsn(match.socialSecurityNumber)}
              </span>
              <span className="ml-2 text-xs text-muted-foreground">
                {match.dateOfBirth ? new Date(match.dateOfBirth).toLocaleDateString() : "—"}
              </span>
            </span>
            <IconArrowRight className="size-4 shrink-0 text-muted-foreground" />
          </button>
        ))}
      </div>
    </div>
  )
}
